// src/components/ui/cards/AppointmentStatusPanel/AppointmentStatusPanelShadcn.jsx
import React, { useState } from 'react';
import {
  Calendar,
  User,
  DollarSign,
  Edit3,
  Trash2,
  CheckCircle2,
  PlayCircle
} from 'lucide-react';

// Import shadcn components
import { Button } from "../../button";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { Badge } from "../../badge";

// Import existing StatusBadge (keep this as it's already working well)
import StatusBadge from '../../feedback/StatusBadge/StatusBadge';

const AppointmentStatusPanelShadcn = ({
  appointment,
  canEdit = true,
  canCancel = true,
  onEdit,
  onCancel,
  onDelete,
  onStatusUpdate,
  loading = false,
  className = ""
}) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    if (!onStatusUpdate) return;

    try {
      setIsUpdatingStatus(true);
      await onStatusUpdate(newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Handle cancellation
  const handleCancel = () => {
    if (!onCancel) return;

    if (confirm(`Are you sure you want to cancel this appointment with ${appointment?.customer?.name || appointment?.customerName}?`)) {
      onCancel();
    }
  };

  // Handle deletion
  const handleDelete = () => {
    if (!onDelete) return;

    if (confirm(`Are you sure you want to delete this appointment? This action cannot be undone.`)) {
      onDelete();
    }
  };

  return (
    <Card className={`bg-slate-800/50 backdrop-blur-xl border-slate-700/50 ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold text-slate-200">Status & Actions</CardTitle>

          <div className="flex items-center gap-2">
            {onEdit && canEdit && (
              <Button
                onClick={onEdit}
                variant="ghost"
                size="sm"
                disabled={loading}
                className="text-slate-400 hover:text-slate-200"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}

            {onCancel && canCancel && appointment?.status !== 'cancelled' && (
              <Button
                onClick={handleCancel}
                variant="destructive"
                size="sm"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}

            {onDelete && (
              <Button
                onClick={handleDelete}
                variant="destructive"
                size="sm"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Current Status */}
        <Card className="bg-slate-700/30 border-slate-600/50">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm font-medium mb-3">Current Status</p>
            <div className="flex items-center justify-between">
              <StatusBadge
                status={appointment?.status || 'pending'}
                size="lg"
              />
              <p className="text-slate-400 text-sm">
                Last updated {appointment?.updatedAt ?
                  new Date(appointment.updatedAt).toLocaleDateString() :
                  'recently'
                }
              </p>
            </div>

            {/* Quick Status Actions */}
            {onStatusUpdate && appointment?.status !== 'cancelled' && (
              <div className="mt-4 space-y-2">
                <p className="text-slate-400 text-sm font-medium">Quick Actions:</p>
                <div className="flex flex-wrap gap-2">
                  {appointment?.status !== 'in_progress' && (
                    <Button
                      onClick={() => handleStatusUpdate('in_progress')}
                      disabled={isUpdatingStatus || loading}
                      variant="outline"
                      size="sm"
                      className="text-blue-400 hover:text-blue-300 border-blue-500/30 hover:border-blue-500/50"
                    >
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Start Work
                    </Button>
                  )}
                  {appointment?.status !== 'completed' && (
                    <Button
                      onClick={() => handleStatusUpdate('completed')}
                      disabled={isUpdatingStatus || loading}
                      variant="outline"
                      size="sm"
                      className="text-emerald-400 hover:text-emerald-300 border-emerald-500/30 hover:border-emerald-500/50"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Complete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Info Cards */}
        <div className="space-y-3">
          {/* Estimated Cost */}
          <Card className="bg-slate-700/30 border-slate-600/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Estimated Cost</p>
                  <p className="text-slate-200 font-medium">
                    {appointment?.estimatedCost || '$275.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assigned Technician */}
          <Card className="bg-slate-700/30 border-slate-600/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <User className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Assigned To</p>
                  <p className="text-slate-200 font-medium">
                    {appointment?.assignedTechnician || 'Mike Rodriguez'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Created Date */}
          <Card className="bg-slate-700/30 border-slate-600/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Calendar className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Created</p>
                  <p className="text-slate-200 font-medium">
                    {appointment?.createdAt ?
                      new Date(appointment.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      }) :
                      'Recently'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppointmentStatusPanelShadcn;